import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, FileText, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api, { formatApiError } from "@/lib/api";

export const QuotationTermsEditor = () => {
  const [clauses, setClauses] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings/quotation-terms").then((r) => setClauses(r.data.clauses)).catch((e) => toast.error(formatApiError(e))); }, []);
  const set = (i, patch) => setClauses((c) => c.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const move = (i, d) => setClauses((c) => { const n = [...c]; const j = i + d; if (j < 0 || j >= n.length) return c; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const save = async () => {
    setSaving(true);
    try { const r = await api.put("/settings/quotation-terms", { clauses }); setClauses(r.data.clauses); toast.success("Terms & Conditions saved"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  if (!clauses) return null;
  return (
    <section className="bg-white border border-slate-200 rounded-lg p-5 mt-5" data-testid="terms-editor">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex gap-3">
          <div className="w-9 h-9 rounded-md bg-indigo-50 text-indigo-700 flex items-center justify-center shrink-0"><FileText className="w-4 h-4" /></div>
          <div>
            <div className="font-semibold text-slate-900">Quotation Terms &amp; Conditions</div>
            <p className="text-sm text-slate-500 mt-1">Printed as numbered clauses on every quotation. A quotation keeps the clauses it was sent with, so later edits never change a sent quote.</p>
          </div>
        </div>
        <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 shrink-0" data-testid="terms-save-btn">{saving ? "Saving..." : "Save"}</Button>
      </div>
      {clauses.length === 0 && <div className="text-sm text-slate-500 border border-dashed rounded-md p-4 mb-3" data-testid="terms-empty">No clauses yet. Add your Terms &amp; Conditions below.</div>}
      <ol className="space-y-3">
        {clauses.map((c, i) => (
          <li key={i} className={`rounded-md border border-slate-200 p-3 ${i % 2 === 0 ? "bg-[#EAF3FF]" : "bg-white"}`} data-testid={`terms-clause-${i}`}>
            <div className="flex items-center gap-2">
              <span className="font-bold text-[#0F1A33] w-6">{i + 1}.</span>
              <Input value={c.title} onChange={(e) => set(i, { title: e.target.value })} placeholder="Clause title" className="bg-white" data-testid={`terms-title-${i}`} />
              <Button type="button" size="sm" variant="ghost" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`terms-up-${i}`}><ArrowUp className="w-4 h-4" /></Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => move(i, 1)} disabled={i === clauses.length - 1} data-testid={`terms-down-${i}`}><ArrowDown className="w-4 h-4" /></Button>
              <Button type="button" size="sm" variant="ghost" className="text-rose-600" onClick={() => setClauses((x) => x.filter((_, j) => j !== i))} data-testid={`terms-del-${i}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
            <Textarea rows={3} value={c.text} onChange={(e) => set(i, { text: e.target.value })} placeholder="Clause text" className="mt-2 bg-white" data-testid={`terms-text-${i}`} />
          </li>
        ))}
      </ol>
      <Button type="button" variant="outline" size="sm" className="mt-3" onClick={() => setClauses((c) => [...c, { title: "", text: "" }])} data-testid="terms-add-btn"><Plus className="w-4 h-4 mr-1" /> Add clause</Button>
    </section>
  );
};
